import { Utilities } from '@/features/base/api/utilities';
import {
  IUserRegistration,
  ILoginCredentials,
  IRefreshCredentials,
  IEmailConfirmation,
  IResetPassword,
  IChangePassword,
  IAuthResponse,
} from '../api/auth.interfaces';
import { authService } from '../api/auth.api';
import { StoreOptions, Module, GetterTree, MutationTree, ActionTree, Commit, Dispatch, ActionContext } from 'vuex';
import { IVuexState } from '@/store/store';

export interface IAuthState {
  token: string;
  userName: string;
  refresh_token: string;
  isAdmin: boolean;
  id: number;
  status: string;
}

const state: IAuthState = {
  token: localStorage.getItem('access_token') || '',
  userName: localStorage.getItem('userName') || '',
  refresh_token: localStorage.getItem('refresh_token') || '',
  isAdmin: localStorage.getItem('isAdmin') === 'true' ? true : false,
  id: (localStorage.getItem('id') || -1) as number,
  status: '',
};

const getters: GetterTree<IAuthState, IVuexState> = {
  authToken: (state: IAuthState) => state.token,
  isAuthenticated: (state: IAuthState) => !!state.token,
  isAdmin: (state: IAuthState) => state.isAdmin,
  authStatus: (state: IAuthState) => state.status,
  userName: (state: IAuthState) => state.userName,
};

const mutations: MutationTree<IAuthState> = {
  reset: (state: IAuthState) => {
    state.token = '';
    state.userName = '';
    state.refresh_token = '';
    state.isAdmin = false;
    state.id = -1;
    state.status = '';
  },

  loading: (state: IAuthState) => {
    state.status = 'Loading';
  },

  success: (state: IAuthState, auth: IAuthResponse) => {
    state.status = 'Success';
    state.token = auth.auth_token.access_token;
    state.userName = auth.auth_token.userName;
    state.refresh_token = auth.auth_token.refresh_token;
    state.isAdmin = auth.auth_token.isAdmin;
    state.id = auth.id;
  },

  error: (state: IAuthState) => {
    state.status = 'Error';
  },

  logout: (state: IAuthState) => {
    state.token = '';
    state.token = '';
    state.userName = '';
    state.refresh_token = '';
    state.isAdmin = false;
    state.id = -1;
  },
};

const actions: ActionTree<IAuthState, IVuexState> = {
  reset: ({ commit }: { commit: Commit }) => {
    commit('reset');
  },

  register: ({ commit }: { commit: Commit }, registration: IUserRegistration) => {
    commit('loading');
    return authService.register(registration);
  },

  confirm: ({ commit }: { commit: Commit }, confirm: IEmailConfirmation) => {
    return authService.confirmEmail(confirm);
  },

  resetPassword: ({ commit }: { commit: Commit }, reset: IResetPassword) => {
    return authService.resetPassword(reset);
  },

  resetPasswordRequest: ({ commit }: { commit: Commit }, { email }: { email: string }) => {
    return authService.sendResetPassword(email);
  },

  changePassword: ({ commit }: { commit: Commit }, changePassword: IChangePassword) => {
    return authService.changePassword(changePassword);
  },

  generateCode: ({ commit }: { commit: Commit }, email: string) => {
    return authService.generateCode(email);
  },

  login: ({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, credentials: ILoginCredentials) => {
    commit('loading');

    return authService
      .login(credentials)
      .then((response: IApi<IAuthResponse>) => {
        Utilities.setLocalStorage(response.result);

        commit('success', response.result);
        dispatch('user/get', null, { root: true });
      })
      .catch((error: any) => {
        commit('error');
        throw error;
      });
  },

  refresh: (context: ActionContext<IAuthState, IVuexState>) => {
    if (context.state.status === 'Loading') {
      return;
    }

    context.commit('loading');

    const credentials: IRefreshCredentials = {
      grant: 'refresh_token',
      refresh_token: state.refresh_token,
    };

    return authService
      .refresh(credentials)
      .then((response: IApi<IAuthResponse>) => {
        const authResponse: IAuthResponse = response && response.result;

        Utilities.setLocalStorage(authResponse);

        context.commit('success', authResponse);
      })
      .catch((error: any) => {
        context.commit('error');
        throw error;
      });
  },

  logout: (context: ActionContext<IAuthState, IVuexState>) => {
    return authService.logout(context.state.refresh_token).then((response: IApi<String>) => {
      context.commit('logout');
      Utilities.clearLocalStorage();
    });
  },
};

const namespaced: boolean = true;

export const auth: Module<IAuthState, IVuexState> = { namespaced, state, getters, actions, mutations };
